import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material'
import requestErrorImg from '../../assets/request-error.svg'
import notFoundImg from '../../assets/not-found.svg'
import accessDeniedErrorImg from '../../assets/access-denied.svg'
import blobImg from '../../assets/blob.svg'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUserStore } from '../../store/user.store'
import { ErrorOutline } from '@mui/icons-material'
import PropTypes from 'prop-types'
import { ERPLoginPageURL, Icons } from '../../utils/utilities'

/**
 * TryAgainMessageBlock is a reusable UI component for displaying error messages, error codes, and retry actions.
 * It supports different error scenarios (401, 403, 404, 500, etc.) and provides context-appropriate actions such as retry, sign-in, or navigation to the home page.
 * The component can be rendered as a full page block or a compact component, and supports custom styles and additional action buttons.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {String} [props.iconSize="24px"] - The size of the error icon.
 * @param {boolean} [props.showErrorIcon=false] - Whether to show the error icon instead of an image.
 * @param {"page"|"component"} [props.parent="page"] - Determines the layout style of the component.
 * @param {Object} [props.styles={}] - Custom styles to apply to the root container.
 * @param {Array<{label: String, action: function, color?: String}>} [props.actions=[]] - Additional action buttons to display.
 * @param {Number} [props.code=500] - The HTTP error code to display (affects image and actions).
 * @param {Function} [props.getData] - Callback function to retry the failed action.
 * @param {String} [props.imageHeight="120px"] - Height of the error image.
 * @param {String} [props.err="Something went wrong! try again."] - The error message to display.
 * @param {boolean} [props.loading] - Whether to show a loading indicator on the retry button.
 * @param {String} [props.btnColor="error"] - The color of the retry button.
 * @returns {JSX.Element} The rendered TryAgainMessageBlock component.
 */
const TryAgainMessageBlock = ( { onBack, iconSize = "24px", showErrorIcon = false, parent = "page", styles = {}, actions = [], code = 500, getData, imageHeight = "120px", err = "Something went wrong! try again.", loading, btnColor = "error" } ) => {

    const navigate = useNavigate()
    const UserStore = useUserStore()
    const { pathname } = useLocation()

    const { palette } = useTheme()

    const navigateToSinginPage = () => {
        const redirectValue = encodeURIComponent( `${window.location.pathname}${window.location.search}` )
        if ( pathname?.includes( "admin" ) )
            navigate( `/admin/?redirect=${redirectValue}` )
        else
            window.location.href = `${ERPLoginPageURL}?redirect=${redirectValue}`

    }

    const navigateToHomePage = () => {
        const user = UserStore?.getUser?.user_role === 'STUDENT' ? 'student' : ["admin", "subadmin"].includes( UserStore?.getUser?.user_role?.toLowerCase() ) ? 'admin' : 'faculty'
        navigate( `/${user}/home` )
    }

    return parent === 'page' ? (
        <Box flexDirection="column" padding={{ md: "40px", xs: "20px" }} display="flex" alignItems="center" justifyContent="center" gap="20px" sx={{ ...styles }}>
            {showErrorIcon ? <ErrorOutline sx={{ marginBottom: "10px", fontSize: iconSize, color: "errorMessage.main" }} /> :
                <Box overflow="clip" height={imageHeight} position="relative">
                    <Box component='img' position="absolute" sx={{ transform: "scale(1.5)", transformOrigin: "center", opacity: 0.7, top: "0", left: 0, width: "100%", height: "100%", zIndex: 0 }} src={blobImg} alt="Something went wrong blob!" />
                    <Box sx={{ position: "relative", zIndex: 1 }} component='img' src={code === 403 ? accessDeniedErrorImg : code === 404 ? notFoundImg : requestErrorImg} height="100%" alt="Something went wrong!" />
                </Box>}

            <Typography textAlign="center" variant='subtitle2' fontWeight="300"><em>{err}</em></Typography>
            <Box display="flex" gap="10px" alignItems="center">
                {code === 401 && <Button disabled={loading} onClick={navigateToSinginPage} startIcon={Icons.default.Login} sx={{ textTransform: "capitalize" }} variant="contained" disableElevation>Sign-in</Button>}
                {code !== 403 && <Button disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : Icons.default.Replay} onClick={getData} sx={{ textTransform: "capitalize" }} color={btnColor} variant="contained" disableElevation>Retry</Button>}
                {code === 403 && <Button disabled={loading} onClick={onBack && typeof onBack === "function" ? onBack : navigateToHomePage} sx={{ textTransform: "capitalize" }} startIcon={Icons.default.ArrowBack} color="success" variant="contained" disableElevation>Go Back</Button>}
                {code === 404 && <Button disabled={loading} onClick={navigateToHomePage} sx={{ textTransform: "capitalize" }} startIcon={Icons.default.ArrowBack} color="success" variant="contained" disableElevation>Back to home</Button>}
                {actions?.length > 0 && actions.map( action => (
                    <Button key={action.label} onClick={action.action} sx={{ textTransform: "capitalize" }} color={action.color} variant="contained" disableElevation>{action.label}</Button>
                ) )}
            </Box>
        </Box>
    ) : (
        <Box justifyContent="center" minHeight="120px" padding="20px" borderRadius="5px" bgcolor={palette.error.light + 22} display="flex" flexDirection="column" gap="2px" alignItems="center" sx={{ ...styles }}>
            <ErrorOutline sx={{ marginBottom: "10px", fontSize: "24px", color: "errorMessage.main" }} />
            <Typography fontSize="12px" textAlign="center" fontWeight={300} variant='subtitle2'><em>{err}</em></Typography>
            <Box display="flex" gap="10px" marginTop="10px" alignItems="center">
                {code === 401 && <Button color="greyed" disabled={loading} onClick={navigateToSinginPage} startIcon={Icons.default.Login} sx={{ textTransform: "capitalize" }} variant='outlined' >Sign-in</Button>}
                <Button color="greyed" startIcon={loading ? <CircularProgress size={14} /> : Icons.default.Replay} onClick={getData} variant='outlined' disabled={loading} sx={{ textTransform: "capitalize" }} >Retry</Button>
            </Box>

        </Box>
    )
}

TryAgainMessageBlock.propTypes = {
    showErrorIcon: PropTypes.bool,
    err: PropTypes.string,
    code: PropTypes.number,
    action: PropTypes.array,
    iconSize: PropTypes.string,
    imageHeight: PropTypes.string,
    parent: PropTypes.oneOf( ["component", "page"] )
}

export default TryAgainMessageBlock
